// Import React
import React, { useState } from "react";

// Import components
import CreateButton from "global/components/CreateButton";
import { ArrowDownCollapseIcon, ArrowUpCollapseIcon } from "global/strich_icons/StrichIcons";

// Import hooks
import useIsMobile from "global/hooks/useIsMobile";



// This returns the plan tone class for the decorative dot
const getPlanToneClassName = (planName = "") => {

    // Normalize the name
    const normalizedName = String(planName).toLowerCase();

    // Return the matching modifier class
    if (normalizedName.includes("bronze")) {

        // Return bronze class
        return "pod-pricing-page__plan-dot--bronze";

    }

    // Return the matching modifier class
    if (normalizedName.includes("silver")) {

        // Return silver class
        return "pod-pricing-page__plan-dot--silver";

    }

    // Return the matching modifier class
    if (normalizedName.includes("gold")) {

        // Return gold class
        return "pod-pricing-page__plan-dot--gold";

    }

    // Return the matching modifier class
    if (normalizedName.includes("platinum")) {

        // Return platinum class
        return "pod-pricing-page__plan-dot--platinum";

    }

    // Return a generic modifier when no named tier matches
    return "pod-pricing-page__plan-dot--default";

};



// This returns the plan tone class for the decorative header strip
const getPlanStripClassName = (planName = "") => {

    // Return the matching dot class with the strip block name
    return getPlanToneClassName(planName).replace("pod-pricing-page__plan-dot", "pod-pricing-page__plan-strip");

};



// This checks whether this plan should be marked as recommended
const planIsSilverRecommendation = (plan, currentPlan) => {

    // Only Silver gets the recommendation badge
    const isSilverPlan = String(plan?.name || "").toLowerCase().includes("silver");

    // Do not show recommendation if this is not Silver
    if (!isSilverPlan) {

        return false;

    }

    // Only recommend Silver when the current plan is lower than Silver
    return Number(currentPlan?.level || 0) < Number(plan?.level || 0);

};



// This formats a plan setting value
const getPlanSettingValue = (plan, settingKey) => {

    // Get the plan settings safely
    const settings = plan?.settings || {};

    // Return the AI concurrent limit
    if (settingKey === "ai_concurrent_limit") {

        // Return unlimited text when the API uses a very large value
        return String(settings?.ai_concurrent_limit) === "999999" ? "Unlimited" : settings?.ai_concurrent_limit || "—";

    }

    // Return the AI retention days
    if (settingKey === "ai_retention_days") {

        // Return the setting value with days
        return settings?.ai_retention_days ? `${settings.ai_retention_days} days` : "—";

    }

    // Return the platform fee
    if (settingKey === "store_platform_fee") {

        // Round the calculated percentage so floating-point precision is not displayed
        return settings?.store_platform_fee !== undefined
            ? `${Number((Number(settings.store_platform_fee) * 100).toFixed(2))}%`
            : "—";

    }

    // Return the payment fee
    if (settingKey === "store_payment_fee") {

        // Round the calculated percentage so floating-point precision is not displayed
        return settings?.store_payment_fee !== undefined
            ? `${Number((Number(settings.store_payment_fee) * 100).toFixed(2))}%`
            : "—";

    }

    // Return fallback value
    return "—";

};



// This renders the subscription plan selector cards
export default function PlanSelector({
    plans,
    currentPlan,
    selectedLevel,
    onSelectPlan,
    showIncludingTax,
    onToggleTaxMode,
    plansCollapsed,
    onTogglePlansCollapsed,
    formatMoney,
    getPackageVisiblePrice,
    getMonthlyEquivalent,
    onOpenUpgrade,
    activeSubscription
}) {

    // Set isMobile from use hook 
    const isMobile = useIsMobile();

    // This safely gets the available upgrades array
    const availableUpgrades = activeSubscription?.available_upgrades || [];

    // Store whether the plan cards should show monthly or yearly billing
    const [selectedBillingPeriod, setSelectedBillingPeriod] = useState("monthly");


    // Define comparison rows in one place so CI changes are easy to review
    const comparisonRows = [
        {
            label: "Queued AI images",
            key: "ai_concurrent_limit",
        },
        {
            label: "AI image retention",
            key: "ai_retention_days",
        },
        {
            label: "Platform fee",
            key: "store_platform_fee",
        },
        {
            label: "Payment fee",
            key: "store_payment_fee",
        },
    ];



    // This handles clicking an upgrade button inside a card
    const handleUpgradeClick = (event, plan, packageKey) => {

        // Stop the card click from also firing
        event.stopPropagation();

        // Stop the browser default button behaviour
        event.preventDefault();

        // Call the parent upgrade handler when available
        onOpenUpgrade?.(plan, packageKey);

    };



    const RenderPlanSelectorHeaderOptions = () => {

        return (

            <div className="pod-pricing-page__plan-selector-options">

                <div className="pod-pricing-page__section-tax-toggle">

                    <button
                        type="button"
                        className={`pod-pricing-page__segmented-button ${selectedBillingPeriod === "monthly" ? "pod-pricing-page__segmented-button--active" : ""}`}
                        onClick={() => setSelectedBillingPeriod("monthly")}
                    >

                        Monthly

                    </button>

                    <button
                        type="button"
                        className={`pod-pricing-page__segmented-button ${selectedBillingPeriod === "yearly" ? "pod-pricing-page__segmented-button--active" : ""}`}
                        onClick={() => setSelectedBillingPeriod("yearly")}
                    >

                        Yearly

                    </button>

                </div>
            
                <div className="pod-pricing-page__section-tax-toggle">

                    <button
                        type="button"
                        className={`pod-pricing-page__segmented-button ${!showIncludingTax ? "pod-pricing-page__segmented-button--active" : ""}`}
                        onClick={() => onToggleTaxMode(false)}
                    >

                        Exc Tax

                    </button>

                    <button
                        type="button"
                        className={`pod-pricing-page__segmented-button ${showIncludingTax ? "pod-pricing-page__segmented-button--active" : ""}`}
                        onClick={() => onToggleTaxMode(true)}
                    >

                        Inc Tax

                    </button>

                </div>
            
            </div>

        )

    };



    // Render plan selector section
    return (

        <section className="pod-pricing-page__plans-section">

            <div className="pod-pricing-page__panel-group pod-pricing-page__panel-group--pricing-redesign">

                <div
                    className="pod-pricing-page__section-heading-row pod-pricing-page__section-heading-row--clickable"
                    role="button"
                    tabIndex={0}
                    onClick={onTogglePlansCollapsed}
                    onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            onTogglePlansCollapsed();
                        }
                    }}
                >

                    <div className="pod-pricing-page__section-heading-copy">

                        <h2 className="pod-pricing-page__section-title">

                            Choose a plan

                        </h2>

                        {/* 
                        <p className="pod-pricing-page__section-text pod-pricing-page__section-text--strong">

                            Compare plans, check monthly and yearly pricing, then upgrade when you are ready.

                        </p>
                        */}

                        
                        
                    </div>

                    <div className="pod-pricing-page__section-heading-actions" onClick={(event) => event.stopPropagation()}>

                        {(!isMobile) && 

                            <RenderPlanSelectorHeaderOptions />

                        }

                        <button
                            type="button"
                            className="pod-pricing-page__icon-button"
                            title={plansCollapsed ? "Expand plans" : "Collapse plans"}
                            onClick={onTogglePlansCollapsed}
                        >

                            {plansCollapsed ? <ArrowDownCollapseIcon /> : <ArrowUpCollapseIcon />}

                        </button>

                    </div>

                    

                </div>

                {(isMobile && !plansCollapsed) && 

                    <RenderPlanSelectorHeaderOptions />

                }

                {plansCollapsed ? (

                    <div className="pod-pricing-page__plan-grid pod-pricing-page__plan-grid--ordered">

                        {plans.map((plan) => {

                            // Check whether this plan is selected
                            const isSelected = Number(selectedLevel) === Number(plan?.level);

                            // Check whether this is the user's current plan
                            const isCurrentPlan = Number(currentPlan?.level) === Number(plan?.level);

                            // Check whether this plan should be recommended
                            const isRecommendedPlan = planIsSilverRecommendation(plan, currentPlan);

                            // Render one collapsed plan pill
                            return (

                                <>

                                    <article
                                        key={plan?.level}
                                        className={`pod-pricing-page__plan-card pod-pricing-page__plan-card--pricing-redesign ${isSelected ? "pod-pricing-page__plan-card--selected" : ""}`}
                                        onClick={() => onSelectPlan(plan?.level)}
                                    >

                                        <div className={`pod-pricing-page__plan-strip ${getPlanStripClassName(plan?.name)}`} />

                                        <div className="pod-pricing-page__plan-card-top-row">

                                            <div className="pod-pricing-page__plan-name-wrap">

                                                <span className={`pod-pricing-page__plan-dot ${getPlanToneClassName(plan?.name)}`} />

                                                <h3 className="pod-pricing-page__plan-name">

                                                    {plan?.name}

                                                </h3>

                                            </div>

                                            {isCurrentPlan && (

                                                <span className="pod-pricing-page__plan-badge">

                                                    Current

                                                </span>

                                            )}

                                            {!isCurrentPlan && isRecommendedPlan && (

                                                <span className="pod-pricing-page__plan-badge pod-pricing-page__plan-badge--recommended">

                                                    Recommended

                                                </span>

                                            )}

                                        </div>

                                    </article>
                                
                                </>

                            );

                        })}

                    </div>

                ) : (

                    <>

                        <div className="pod-pricing-page__plan-grid pod-pricing-page__plan-grid--ordered">

                            {plans.map((plan) => {

                                // Check whether this plan is selected
                                const isSelected = Number(selectedLevel) === Number(plan?.level);

                                // Check whether this is the user's current plan
                                const isCurrentPlan = Number(currentPlan?.level) === Number(plan?.level);

                                // Check whether this plan should be recommended
                                const isRecommendedPlan = planIsSilverRecommendation(plan, currentPlan);

                                // Get monthly package for display
                                const monthlyPackage = plan?.packages?.monthly;

                                // Check if this upgrade is available
                                const allowMonthlyUpgrade = monthlyPackage ? availableUpgrades?.some((upgrade) => upgrade.code === monthlyPackage.code) : false;

                                // Get yearly package for display
                                const yearlyPackage = plan?.packages?.yearly;

                                // Check whether this is a same-level monthly to yearly package switch
                                const isCurrentMonthlyToYearlySwitch = isCurrentPlan
                                    && selectedBillingPeriod === "yearly"
                                    && activeSubscription?.subscription_package?.includes("_monthly")
                                    && yearlyPackage?.code
                                    && yearlyPackage.code !== activeSubscription?.subscription_package;

                                // Check if this upgrade is available
                                const allowYearlyUpgrade = yearlyPackage ? availableUpgrades?.some((upgrade) => upgrade.code === yearlyPackage.code) : false;

                                // Select the package for the chosen billing period
                                const selectedPackage = selectedBillingPeriod === "yearly" ? yearlyPackage : monthlyPackage;

                                // Check whether the selected package can be upgraded to
                                const allowSelectedUpgrade = selectedBillingPeriod === "yearly" ? allowYearlyUpgrade : allowMonthlyUpgrade;

                                // Check whether the selected package should be actionable
                                const allowSelectedPackageAction = allowSelectedUpgrade || (isCurrentMonthlyToYearlySwitch && allowYearlyUpgrade);

                                // Set the button title
                                const selectedPackageButtonTitle = isCurrentPlan
                                    ? isCurrentMonthlyToYearlySwitch && allowYearlyUpgrade ? "Switch to yearly" : "Current plan"
                                    : "Upgrade";

                                // Calculate selected package display price
                                const selectedPackagePrice = selectedPackage ? formatMoney(getPackageVisiblePrice(selectedPackage, showIncludingTax)) : null;

                                // Calculate yearly equivalent monthly price
                                const yearlyEquivalent = yearlyPackage ? formatMoney(getMonthlyEquivalent(yearlyPackage, showIncludingTax)) : null;

                                // Render one plan card
                                return (

                                    <article
                                        key={plan?.level}
                                        className={`pod-pricing-page__plan-card pod-pricing-page__plan-card--pricing-redesign ${isSelected ? "pod-pricing-page__plan-card--selected" : ""}`}
                                        onClick={() => onSelectPlan(plan?.level)}
                                    >

                                        <div className={`pod-pricing-page__plan-strip ${getPlanStripClassName(plan?.name)}`} />

                                        <div className="pod-pricing-page__plan-card-top-row">

                                            <div className="pod-pricing-page__plan-name-wrap">

                                                <span className={`pod-pricing-page__plan-dot ${getPlanToneClassName(plan?.name)}`} />

                                                <h3 className="pod-pricing-page__plan-name">

                                                    {plan?.name}

                                                </h3>

                                            </div>

                                            {isCurrentPlan && (

                                                <span className="pod-pricing-page__plan-badge">

                                                    Current

                                                </span>

                                            )}

                                            {!isCurrentPlan && isRecommendedPlan && (

                                                <span className="pod-pricing-page__plan-badge pod-pricing-page__plan-badge--recommended">

                                                    Recommended

                                                </span>

                                            )}

                                        </div>

                                        <p className="pod-pricing-page__plan-description pod-pricing-page__plan-description--strong">

                                            {plan?.description}

                                        </p>

                                        {selectedPackage && (

                                            <div className="pod-pricing-page__plan-prices pod-pricing-page__plan-prices--redesign">

                                                <div className={`pod-pricing-page__plan-price-block pod-pricing-page__plan-price-block--redesign ${selectedBillingPeriod === "yearly" ? "pod-pricing-page__plan-price-block--featured" : ""}`}>

                                                    <div className="pod-pricing-page__plan-price-block__container">

                                                        <span className="pod-pricing-page__plan-price-label">

                                                            {selectedBillingPeriod === "yearly" ? "Yearly" : "Monthly"}

                                                        </span>

                                                        <strong className="pod-pricing-page__plan-price-value">

                                                            {selectedPackagePrice}

                                                        </strong>

                                                        <span className="pod-pricing-page__plan-price-subvalue">

                                                            {selectedBillingPeriod === "yearly"
                                                                ? `${yearlyEquivalent} / month equivalent`
                                                                : "Billed every month"}

                                                        </span>

                                                    </div>

                                                    <CreateButton
                                                        title={selectedPackageButtonTitle}
                                                        type="button"
                                                        size="xs"
                                                        invert={true}
                                                        plain={isCurrentPlan && !allowSelectedPackageAction}
                                                        disabled={!allowSelectedPackageAction}
                                                        onClick={(event) => handleUpgradeClick(event, plan, selectedBillingPeriod)}
                                                    />

                                                </div>

                                            </div>

                                        )}

                                    </article>

                                );

                            })}

                        </div>

                    </>

                )}

                <div className="pod-pricing-page__comparison-panel">

                    <div className="pod-pricing-page__comparison-heading">

                        <h3 className="pod-pricing-page__comparison-title">

                            Compare features

                        </h3>

                        <p className="pod-pricing-page__comparison-text">

                            Plan limits and fees are easier to compare side by side.

                        </p>

                    </div>

                    <div className="pod-pricing-page__comparison-table-wrap">

                        <table className="pod-pricing-page__comparison-table">

                            <thead>

                                <tr>

                                    <th>

                                        Feature

                                    </th>

                                    {plans.map((plan) => (

                                        <th key={plan?.level}>

                                            <div className="pod-pricing-page__plan-pill--left">

                                                <span className={`pod-pricing-page__plan-dot ${getPlanToneClassName(plan?.name)}`} />

                                                <span className="pod-pricing-page__plan-pill-title">

                                                    {plan?.name}

                                                </span>

                                            </div>

                                        </th>

                                    ))}

                                </tr>

                            </thead>

                            <tbody>

                                {comparisonRows.map((row) => (

                                    <tr key={row.key}>

                                        <td>

                                            {row.label}

                                        </td>

                                        {plans.map((plan) => {

                                            // Check whether this is the selected plan
                                            const isSelected = Number(selectedLevel) === Number(plan?.level);

                                            // Return the comparison value cell
                                            return (

                                                <td
                                                    key={`${plan?.level}-${row.key}`}
                                                    className={isSelected ? "pod-pricing-page__comparison-cell--selected" : ""}
                                                >

                                                    {getPlanSettingValue(plan, row.key)}

                                                </td>

                                            );

                                        })}

                                    </tr>

                                ))}

                            </tbody>

                        </table>

                    </div>

                </div>

            </div>

        </section>

    );

}